import { useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useChangePassword } from '@/features/auth/hooks/useChangePassword'
import { meQueryKey } from '@/features/settings/hooks/useMe'
import { Button, Input, Card } from '@/components/ui'
import { PASSWORD_HINT, newPasswordSchema } from '@/lib/passwordPolicy'
import { getErrorMessage } from '@/lib/getErrorMessage'

const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1, 'Current password is required'),
    newPassword: newPasswordSchema,
    confirmNewPassword: z.string().min(1, 'Please confirm your new password'),
  })
  .refine((data) => data.newPassword === data.confirmNewPassword, {
    message: 'New passwords do not match',
    path: ['confirmNewPassword'],
  })
  .refine((data) => data.currentPassword !== data.newPassword, {
    message: 'New password must be different from current password',
    path: ['newPassword'],
  })

type ChangePasswordFormValues = z.infer<typeof changePasswordSchema>

/**
 * Form for changing the current user's password.
 */
export function ChangePasswordForm(): React.ReactElement {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ChangePasswordFormValues>({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmNewPassword: '',
    },
  })

  const queryClient = useQueryClient()
  const mutation = useChangePassword()

  const onSubmit = (values: ChangePasswordFormValues): void => {
    mutation.mutate(
      {
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      },
      {
        onSuccess: () => {
          reset()
          queryClient.invalidateQueries({ queryKey: meQueryKey })
        },
      }
    )
  }

  // An AxiosError is also an Error, so reading `.message` showed "Request
  // failed with status code 400" instead of the server's reason.
  const apiError = mutation.error == null ? null : getErrorMessage(mutation.error)

  return (
    <Card
      className="w-full max-w-md"
      header={
        <div>
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
            Change password
          </h3>
          <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
            Use a strong password that you do not use elsewhere.
          </p>
        </div>
      }
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {apiError && (
          <p className="text-sm text-danger" role="alert">
            {apiError}
          </p>
        )}
        {mutation.isSuccess && (
          <p
            className="flex items-center gap-2 text-sm text-green-700 dark:text-green-400"
            role="status"
          >
            <span aria-hidden className="text-green-600 dark:text-green-400">
              ✓
            </span>
            Password updated successfully.
          </p>
        )}
        <Input
          label="Current password"
          type="password"
          autoComplete="current-password"
          showPasswordToggle
          error={errors.currentPassword?.message}
          {...register('currentPassword')}
        />
        <Input
          label="New password"
          type="password"
          autoComplete="new-password"
          showPasswordToggle
          hint={PASSWORD_HINT}
          error={errors.newPassword?.message}
          {...register('newPassword')}
        />
        <Input
          label="Confirm new password"
          type="password"
          autoComplete="new-password"
          showPasswordToggle
          error={errors.confirmNewPassword?.message}
          {...register('confirmNewPassword')}
        />
        <Button
          type="submit"
          className="w-full"
          loading={mutation.isPending}
        >
          {mutation.isPending ? 'Updating…' : 'Update password'}
        </Button>
      </form>
    </Card>
  )
}

ChangePasswordForm.displayName = 'ChangePasswordForm'
